import { Box, useTheme } from '@mui/joy';
import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ContactGroupType } from 'contacts/enums';
import { IContactGroupAddress } from 'contacts/types';
import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { getAddressToDisplay } from 'core/utils/address';
import { Size, Variant } from 'theme/enums';

import { LocationField } from './LocationField';
import { IFieldConfig, IMergeLocationsForm, TDisabledFields } from './types';
import { getAddressValueFromLocation } from './utils';

interface IProps {
  isOpen: boolean;
  onSubmit: (reset?: UseFormReset<IMergeLocationsForm>) => (data: IMergeLocationsForm) => void;
  onClose: (reset?: UseFormReset<IMergeLocationsForm>) => void;
  locations: IContactGroupAddress[];
  contactGroupTypes: ContactGroupType[];
  disabledFields: TDisabledFields;
}

export const MergeLocationsForm = ({
  isOpen,
  onSubmit,
  onClose,
  locations,
  contactGroupTypes,
  disabledFields,
}: IProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const { setValue, watch } = useFormContext();

  const locationFields = useMemo(() => {
    const baseFields = [
      {
        name: 'name',
        label: t('labelLocationName'),
        valueKey: 'name',
        labelKey: 'name',
        required: true,
      },
      {
        name: 'address',
        label: t('labelAddress'),
        getValueKey: (location: IContactGroupAddress) => getAddressValueFromLocation(location),
        getLabelKey: (location: IContactGroupAddress) => (
          <Box sx={{ textAlign: 'left' }}>
            {getAddressToDisplay(location, {
              withAccountsReceivable: false,
              displayStateName: true,
              displayCountryName: true,
            }).join(', ')}
          </Box>
        ),
        required: true,
      },
    ];

    if (contactGroupTypes.includes(ContactGroupType.CUSTOMER)) {
      baseFields.push({
        name: 'externalCustomerNumber',
        label: t('labelCustomerNumber'),
        valueKey: 'externalCustomerNumber',
        labelKey: 'externalCustomerNumber',
        required: true,
      });

      baseFields.push({
        name: 'accountsReceivableId',
        label: t('labelARNumber'),
        valueKey: 'accountsReceivableId',
        labelKey: 'accountsReceivableId',
        required: true,
      });
    }

    if (contactGroupTypes.includes(ContactGroupType.VENDOR)) {
      baseFields.push({
        name: 'accountsPayableId',
        label: t('labelAPNumber'),
        valueKey: 'accountsPayableId',
        labelKey: 'accountsPayableId',
        required: true,
      });
    }

    return baseFields;
  }, [contactGroupTypes, t]);

  const isShipToValue = watch('isShipTo');
  const isBillToValue = watch('isBillTo');

  const switchSxProps = (isChecked: boolean) => ({
    sx: {
      '--Switch-trackBackground': isChecked
        ? `${theme.palette.primary.softBg} !important`
        : theme.palette.neutral[400],
    },
    labelSx: {
      color: theme.palette.neutral[isChecked ? 800 : 600],
    },
  });

  return (
    <Form
      title={t('titleMergeLocations')}
      subtitle={t('subtitleMergeLocations')}
      onSubmit={onSubmit}
      onClose={onClose}
      isOpen={isOpen}
      submitBtnLabel={t('buttonLabelMergeLocations')}
    >
      <>
        {locationFields.map((field) => (
          <LocationField
            key={field.name}
            field={field as IFieldConfig}
            locations={locations}
            watch={watch}
            setValue={setValue}
            disabled={!!disabledFields?.[field.name]}
          />
        ))}

        {contactGroupTypes.includes(ContactGroupType.CUSTOMER) && (
          <>
            <FormField
              name="isShipTo"
              type={InputType.SWITCH}
              inputProps={{
                label: t('labelShipTo'),
                size: Size.MD,
                variant: isShipToValue ? Variant.SOFT : Variant.SOLID,
                ...switchSxProps(isShipToValue),
                disabled: disabledFields?.isShipTo,
              }}
            />

            <FormField
              name="isBillTo"
              type={InputType.SWITCH}
              inputProps={{
                label: t('labelBillTo'),
                size: Size.MD,
                variant: isBillToValue ? Variant.SOFT : Variant.SOLID,
                ...switchSxProps(isBillToValue),
                disabled: disabledFields?.isBillTo,
              }}
            />
          </>
        )}
      </>
    </Form>
  );
};
